import { useState } from 'react';
import { BookOpen, Users, ExternalLink, Loader2, AlertCircle, Calendar } from 'lucide-react';

// Vercel sets REACT_APP_API_BASE; empty default = same-origin (self-hosted build)
const API_BASE = process.env.REACT_APP_API_BASE || '';

export default function Home() {
  const [currentView, setCurrentView] = useState('years');
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedTrimester, setSelectedTrimester] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [yearData, setYearData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingYear, setLoadingYear] = useState(null);
  const [error, setError] = useState(null);

  const fetchYearData = async (year) => {
    setLoading(true);
    setLoadingYear(year);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/values?year=${year}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 503) {
          setError(`Server is warming up. Please wait a moment and try again.`);
        } else {
          throw new Error('Failed to fetch data');
        }
        return;
      }

      const data = await response.json();
      
      if (!data || data.length === 0) {
        setError(`No study guides available for ${year} yet.`);
        return;
      }

      setYearData(data[0]);
      setSelectedYear(year);
      setCurrentView('trimesters');
    } catch (err) {
      console.error('Fetch error:', err);
      setError(`Error loading ${year} data. The server may be starting up - please try again in a moment.`);
    } finally {
      setLoading(false);
      setLoadingYear(null);
    }
  };

  const handleYearClick = (year) => {
    fetchYearData(year);
  };

  const handleTrimesterClick = (trimester) => {
    setSelectedTrimester(trimester);
    setCurrentView('classes');
  };

  const handleClassClick = (className) => {
    setSelectedClass(className);
    setCurrentView('guides');
  };

  const handleBack = () => {
    if (currentView === 'guides') {
      setSelectedClass(null);
      setCurrentView('classes');
    } else if (currentView === 'classes') {
      setSelectedTrimester(null);
      setCurrentView('trimesters');
    } else if (currentView === 'trimesters') {
      setYearData(null);
      setSelectedYear(null);
      setCurrentView('years');
    }
  };

  const YearSelection = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-12">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-indigo-600" />
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Dental Study Guides</h1>
          <p className="text-gray-600">Select your year to access study materials</p>
        </div>

        <div className="text-center mb-6">
          <a
            href="/cms"
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Admin Login →
          </a>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {['D1', 'D2', 'D3', 'D4'].map((year) => (
            <button
              key={year}
              onClick={() => handleYearClick(year)}
              disabled={loading}
              className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative"
            >
              {loadingYear === year ? (
                <div className="flex flex-col items-center justify-center">
                  <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-3" />
                  <p className="text-sm text-gray-600">Loading {year}...</p>
                </div>
              ) : (
                <>
                  <div className="text-5xl font-bold text-indigo-600 mb-2">{year}</div>
                  <p className="text-gray-600">Year {year.slice(1)}</p>
                </>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const TrimesterSelection = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto py-8">
        <button
          onClick={handleBack}
          className="mb-6 px-4 py-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-gray-700"
        >
          ← Back to Years
        </button>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">{selectedYear} - Select Trimester</h2>
          <p className="text-gray-600">Choose a trimester to view classes</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {yearData && Object.keys(yearData.Trimesters).sort().map((trimester) => (
            <button
              key={trimester}
              onClick={() => handleTrimesterClick(trimester)}
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all transform hover:scale-105"
            >
              <Calendar className="w-8 h-8 text-indigo-600 mb-3 mx-auto" />
              <h3 className="text-2xl font-bold text-gray-800 mb-1">Trimester {trimester}</h3>
              <p className="text-gray-600 text-sm">
                {Object.keys(yearData.Trimesters[trimester]).length} class(es)
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const ClassSelection = () => {
    const classes = yearData?.Trimesters[selectedTrimester] || {};

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-4xl mx-auto py-8">
          <button
            onClick={handleBack}
            className="mb-6 px-4 py-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-gray-700"
          >
            ← Back to Trimesters
          </button>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              {selectedYear} - Trimester {selectedTrimester}
            </h2>
            <p className="text-gray-600">Select a class to view study guides</p>
          </div>

          {loading ? (
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-indigo-600" />
              <p className="text-gray-600">Loading classes...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.keys(classes).map((className) => (
                <button
                  key={className}
                  onClick={() => handleClassClick(className)}
                  className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all transform hover:scale-105"
                >
                  <Users className="w-8 h-8 text-indigo-600 mb-3" />
                  <h3 className="text-xl font-bold text-gray-800 mb-1">Class {className}</h3>
                  <p className="text-gray-600 text-sm">
                    {Object.keys(classes[className]).length} study guide(s)
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const StudyGuides = () => {
    const guides = yearData?.Trimesters[selectedTrimester]?.[selectedClass] || {};

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-4xl mx-auto py-8">
          <button
            onClick={handleBack}
            className="mb-6 px-4 py-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-gray-700"
          >
            ← Back to Classes
          </button>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              {selectedYear} - Trimester {selectedTrimester} - Class {selectedClass}
            </h2>
            <p className="text-gray-600">Available study guides</p>
          </div>

          <div className="space-y-4">
            {Object.entries(guides).map(([guideName, guideUrl]) => (
              <a
                key={guideName}
                href={guideUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all transform hover:scale-102 group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-1">
                      {guideName}
                    </h3>
                    <p className="text-sm text-gray-500 truncate max-w-xl">{guideUrl}</p>
                  </div>
                  <ExternalLink className="w-6 h-6 text-indigo-600 group-hover:text-indigo-700 flex-shrink-0" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {currentView === 'years' && <YearSelection />}
      {currentView === 'trimesters' && <TrimesterSelection />}
      {currentView === 'classes' && <ClassSelection />}
      {currentView === 'guides' && <StudyGuides />}
    </>
  );
}